import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider
} from '@mui/material'
import {
  History,
  Visibility,
  Compare,
  Restore,
  Download
} from '@mui/icons-material'
import { useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { format } from 'date-fns'
import medicalCodingService from '../../../services/medicalCodingService'

const CodingVersionHistory = () => {
  const { codingId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  
  const [loading, setLoading] = useState(false)
  const [versions, setVersions] = useState([])
  const [selectedVersion, setSelectedVersion] = useState(null)
  const [compareVersion, setCompareVersion] = useState(null)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [compareDialogOpen, setCompareDialogOpen] = useState(false)

  useEffect(() => {
    if (codingId) {
      loadVersionHistory()
    }
  }, [codingId])

  const loadVersionHistory = async () => {
    setLoading(true)
    try {
      const response = await medicalCodingService.getCodingVersions(codingId)
      
      if (response.data.success) {
        setVersions(response.data.data || [])
      }
    } catch (error) {
      enqueueSnackbar('Failed to load version history', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleViewVersion = (version) => {
    setSelectedVersion(version)
    setViewDialogOpen(true)
  }

  const handleCompareVersions = (version1, version2) => {
    setSelectedVersion(version1)
    setCompareVersion(version2)
    setCompareDialogOpen(true)
  }

  const handleRestoreVersion = async (versionId) => {
    if (window.confirm('Are you sure you want to restore this version? This will create a new version with the restored data.')) {
      try {
        // Implementation would depend on backend API
        enqueueSnackbar('Version restored successfully', { variant: 'success' })
        loadVersionHistory()
      } catch (error) {
        enqueueSnackbar('Failed to restore version', { variant: 'error' })
      }
    }
  }

  const renderCodingData = (coding) => {
    if (!coding) return null

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Primary Diagnosis
          </Typography>
          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="body2">
              <strong>Code:</strong> {coding.primaryDiagnosis?.icd10Code || 'N/A'}
            </Typography>
            <Typography variant="body2">
              <strong>Description:</strong> {coding.primaryDiagnosis?.icd10Description || 'N/A'}
            </Typography>
            <Typography variant="body2">
              <strong>Clinical Diagnosis:</strong> {coding.primaryDiagnosis?.diagnosis || 'N/A'}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Secondary Diagnoses ({coding.secondaryDiagnoses?.length || 0})
          </Typography>
          <Paper sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 200, overflow: 'auto' }}>
            {coding.secondaryDiagnoses?.map((diagnosis, index) => (
              <Box key={index} sx={{ mb: 1 }}>
                <Typography variant="body2">
                  {index + 1}. {diagnosis.icd10Code} - {diagnosis.icd10Description}
                </Typography>
              </Box>
            )) || <Typography variant="body2" color="text.secondary">None</Typography>}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Procedures ({coding.procedures?.length || 0})
          </Typography>
          <Paper sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 200, overflow: 'auto' }}>
            {coding.procedures?.map((procedure, index) => (
              <Box key={index} sx={{ mb: 1 }}>
                <Typography variant="body2">
                  {index + 1}. {procedure.icd10pcsCode} - {procedure.description}
                </Typography>
              </Box>
            )) || <Typography variant="body2" color="text.secondary">None</Typography>}
          </Paper>
        </Grid>

        {coding.drgGroup && (
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              DRG Information
            </Typography>
            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="body2">
                <strong>Code:</strong> {coding.drgGroup.code}
              </Typography>
              <Typography variant="body2">
                <strong>Description:</strong> {coding.drgGroup.description}
              </Typography>
              <Typography variant="body2">
                <strong>Weight:</strong> {coding.drgGroup.weight}
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    )
  }

  if (loading) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Loading Version History...
        </Typography>
        <LinearProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <History sx={{ mr: 2 }} />
        <Typography variant="h6" fontWeight={600}>
          Coding Version History
        </Typography>
      </Box>

      {versions.length === 0 ? (
        <Alert severity="info">
          No version history found for this coding record.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Version</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Modified By</TableCell>
                <TableCell>Date & Time</TableCell>
                <TableCell>Changes</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {versions.map((version, index) => (
                <TableRow key={version._id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight={600}>
                        v{version.version || index + 1}
                      </Typography>
                      {index === 0 && (
                        <Chip
                          label="Current"
                          color="primary"
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Chip
                      label={version.verificationStatus || 'PENDING'}
                      color={
                        version.verificationStatus === 'VERIFIED' ? 'success' :
                        version.verificationStatus === 'PENDING' ? 'warning' : 'error'
                      }
                      size="small"
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {version.codedBy?.name || 'Unknown'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {version.codedBy?.email}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {(version.codedAt || version.createdAt) ? format(new Date(version.codedAt || version.createdAt), 'MMM dd, yyyy HH:mm') : 'N/A'}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {version.changesSummary || 'No summary available'}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => handleViewVersion(version)}
                      >
                        View
                      </Button>
                      
                      {index > 0 && (
                        <>
                          <Button
                            size="small"
                            startIcon={<Compare />}
                            onClick={() => handleCompareVersions(versions[0], version)}
                          >
                            Compare
                          </Button>
                          
                          <Button
                            size="small"
                            startIcon={<Restore />}
                            onClick={() => handleRestoreVersion(version._id)}
                          >
                            Restore
                          </Button>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* View Version Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Version Details - v{selectedVersion?.version}
        </DialogTitle>
        <DialogContent>
          {renderCodingData(selectedVersion)}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<Download />}
            onClick={() => {
              // Export version data
            }}
          >
            Export
          </Button>
          <Button onClick={() => setViewDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Compare Versions Dialog */}
      <Dialog
        open={compareDialogOpen}
        onClose={() => setCompareDialogOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          Compare Versions
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom color="primary">
                Current Version (v{selectedVersion?.version})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {renderCodingData(selectedVersion)}
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom color="secondary">
                Previous Version (v{compareVersion?.version})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {renderCodingData(compareVersion)}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompareDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CodingVersionHistory